import { FunctionComponent, useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { IntlShape } from 'react-intl';
import { Menu } from 'lucide-react';
import { MapPinIcon, CurrencyDollarIcon, BuildingOffice2Icon, BriefcaseIcon } from '@heroicons/react/24/solid';
import ReactGA from 'react-ga4';
import { usePageContext } from 'vike-react/usePageContext';
import { AutocompleteHandle } from '../Autocomplete';
import Avatar from '../Avatar';
import { LazyTooltip } from '../Tooltip';
import { SearchNavbar } from './SearchNavbar';
import { FilterCategoryButtons } from '../filters/FilterCategoryButtons';
import { TopFilters } from '../filters/TopFilters';
import { useSearchFilters, useSearchQuery } from '../../contexts/SearchContext';
import { useFacets } from '../../contexts/FacetsContext';
import { convertUrlToLanguage } from '../../utils/algolia/url-handling';

interface DesktopFilterCategoriesProps {
  intl: IntlShape;
  activeFilterModal: string | null;
  setActiveFilterModal: (filter: string | null) => void;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  isAllFiltersModalOpen?: boolean;
  setIsAllFiltersModalOpen?: (open: boolean) => void;
  clearSearch: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  user?: any;
  closeAutocomplete?: () => void;
  autocompleteRef?: React.RefObject<AutocompleteHandle | null>;
  onAutocompleteOpen?: () => void;
}

const DesktopFilterCategories: FunctionComponent<DesktopFilterCategoriesProps> = ({
  intl,
  activeFilterModal,
  setActiveFilterModal,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isAllFiltersModalOpen,
  setIsAllFiltersModalOpen,
  clearSearch,
  user,
  closeAutocomplete,
  autocompleteRef,
  onAutocompleteOpen,
}) => {
  const { filters } = useSearchFilters();
  const pageContext = usePageContext();
  const locale = (pageContext?.locale || 'en') as 'en' | 'fi' | 'sv';
  
  // State for language selector dropdown (xl screens and above)
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  
  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
    };
    if (isLanguageMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isLanguageMenuOpen]);
  
  // Facet computation is now handled by TopFilters component
  const { query } = useSearchQuery();
  const filtersObj = (typeof filters === 'object' && filters) ? filters as Record<string, string[]> : {};
  const { facetsData } = useFacets();

  // Get location items for filtering
  const locationItems = facetsData.location || [];
  
  // Filter location facets to only show Finland, Remote, or Global locations
  // Always include active (selected) filters even if they don't match keywords
  const filterLocationFacets = useCallback((items: Array<{ value: string; label: string; count: number }>) => {
    const allowedKeywords = [
      // Finland
      'finland', 'suomi', 'finländ',
      // Remote
      'remote', 'etätyö', 'etä', 'distans', 'fjärr',
      // Global
      'global', 'maailmanlaajuinen', 'världsomfattande',
    ];
    
    // Exclude "Remote - United States" and its translations
    const excludedPatterns = [
      'remote - united states',
      'etätyö - yhdysvallat',
      'etä - yhdysvallat',
      'distans - usa',
      'fjärr - usa',
      'distans - förenta staterna',
      'fjärr - förenta staterna',
    ];
    
    const activeLocationValues = filtersObj.location || [];
    
    return items.filter(item => {
      // Always include if it's an active filter
      if (activeLocationValues.includes(item.value)) {
        return true;
      }
      
      const lowerValue = item.value.toLowerCase();
      const lowerLabel = item.label.toLowerCase();
      
      // Exclude "Remote - United States" and its translations
      if (excludedPatterns.some(pattern => 
        lowerValue.includes(pattern) || lowerLabel.includes(pattern)
      )) {
        return false;
      }
      
      // Check if value or label contains any of the allowed keywords
      return allowedKeywords.some(keyword => 
        lowerValue.includes(keyword) || lowerLabel.includes(keyword)
      );
    });
  }, [filtersObj.location]);
  
  // Memoize filtered location items
  const filteredLocationItems = useMemo(() => filterLocationFacets(locationItems), [filterLocationFacets, locationItems]);
  
  // Category buttons (top row)
  type FilterCategory = {
    id: string;
    label: string;
    icon?: React.ReactNode;
    colorClass: string;
    hasFilters: boolean;
  };
  
  const handleCategoryClick = useCallback((categoryId: string) => {
    // Close autocomplete when filter button is clicked
    closeAutocomplete?.();
    
    if (activeFilterModal === categoryId) {
      setActiveFilterModal(null);
    } else {
      setActiveFilterModal(categoryId);
    }
  }, [activeFilterModal, setActiveFilterModal, closeAutocomplete]);
  
  const categories: FilterCategory[] = [
    {
      id: 'location',
      label: intl.formatMessage({ id: 'search.refinements.location', defaultMessage: 'Location', description: 'Filter label for location selection in search refinements' }),
      icon: <MapPinIcon className="w-4 h-4" />,
      colorClass: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700',
      hasFilters: !!(filtersObj.location?.length || filtersObj.remoteness?.length),
    },
    {
      id: 'salary',
      label: intl.formatMessage({ id: 'search.refinements.salary', defaultMessage: 'Salary', description: 'Filter label for salary range selection in search refinements' }),
      icon: <CurrencyDollarIcon className="w-4 h-4" />,
      colorClass: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700',
      hasFilters: !!(filtersObj.salaryRangeMin?.length || filtersObj.vacationWeeks?.length),
    },
    {
      id: 'company',
      label: intl.formatMessage({ id: 'search.refinements.company', defaultMessage: 'Company', description: 'Filter label for company selection in search refinements' }),
      icon: <BuildingOffice2Icon className="w-4 h-4" />,
      colorClass: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700',
      hasFilters: !!(filtersObj.companyId?.length || filtersObj.companyIndustry?.length),
    },
    {
      id: 'job',
      label: intl.formatMessage({ id: 'search.refinements.job', defaultMessage: 'Job', description: 'Filter label for job-related filters (type, title, etc.) in search refinements' }),
      icon: <BriefcaseIcon className="w-4 h-4" />,
      colorClass: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700',
      hasFilters: !!(
        filtersObj.jobType?.length || 
        filtersObj.normalisedRoleLevel?.length || 
        filtersObj.requiredLanguages?.length || 
        filtersObj.benefits?.length || 
        filtersObj.skillsAndTechnologies?.length ||
        filtersObj.field?.length ||
        filtersObj.educationLevel?.length ||
        filtersObj.peopleManagementTier?.length ||
        filtersObj.lastSeenMin?.length ||
        filtersObj.excludeSaved?.length ||
        filtersObj.excludeViewed?.length ||
        filtersObj.excludeApplied?.length
      ),
    },
  ];

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-[45]" role="banner">
      {/* Top row: Logo + Searchbar + Filter Categories + Hamburger Menu (lg and above) */}
      <nav className="hidden lg:flex items-center gap-3 h-14 min-h-14 py-3 px-4 xl:px-20 md:px-10 border-b border-gray-200 dark:border-gray-700" aria-label="Main navigation">
        {/* Left side: Logo + Searchbar */}
        <SearchNavbar
          intl={intl}
          clearSearch={clearSearch}
          autocompleteRef={autocompleteRef}
          onAutocompleteOpen={onAutocompleteOpen}
        />
        
        {/* Center: Filter Category buttons */}
        <FilterCategoryButtons
          intl={intl}
          categories={categories}
          activeCategory={activeFilterModal}
          onCategoryClick={handleCategoryClick}
          className="flex-1 justify-center"
        />
        
        {/* Right side: Language Selector (xl screens and above) and Hamburger Menu */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          {/* Language Selector - only visible on xl screens and above */}
          <div ref={languageMenuRef} className="hidden xl:block relative cursor-pointer">
            <LazyTooltip
              content={intl.formatMessage({ id: 'tooltip.language', defaultMessage: 'Change website language (English, Finnish, Swedish)', description: 'Tooltip text for language selector button' })}
              position="bottom"
            >
              <button
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                aria-label={intl.formatMessage({ id: 'navigation.language', defaultMessage: 'Language', description: 'Button aria-label for language selector in navigation' })}
              >
                {locale === 'fi' && <span className="text-xs">FI</span>}
                {locale === 'sv' && <span className="text-xs">SV</span>}
                {locale === 'en' && <span className="text-xs">EN</span>}
              </button>
            </LazyTooltip>
            {isLanguageMenuOpen && (
              <div className="absolute top-full right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                <button
                  onClick={() => {
                    ReactGA.event({
                      category: 'interactions',
                      action: 'click_change_language',
                      label: 'fi',
                      nonInteraction: false,
                    });
                    window.location.href = convertUrlToLanguage(window.location.pathname, 'fi', intl);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
                    locale === 'fi'
                      ? 'bg-green-100 text-green-900 dark:bg-green-200 dark:text-black font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span>FI</span>
                </button>
                <button
                  onClick={() => {
                    ReactGA.event({
                      category: 'interactions',
                      action: 'click_change_language',
                      label: 'sv',
                      nonInteraction: false,
                    });
                    window.location.href = convertUrlToLanguage(window.location.pathname, 'sv', intl);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
                    locale === 'sv'
                      ? 'bg-green-100 text-green-900 dark:bg-green-200 dark:text-black font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span>SV</span>
                </button>
                <button
                  onClick={() => {
                    ReactGA.event({
                      category: 'interactions',
                      action: 'click_change_language',
                      label: 'en',
                      nonInteraction: false,
                    });
                    window.location.href = convertUrlToLanguage(window.location.pathname, 'en', intl);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
                    locale === 'en'
                      ? 'bg-green-100 text-green-900 dark:bg-green-200 dark:text-black font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span>EN</span>
                </button>
              </div>
            )}
          </div>
          <button
            className={`flex items-center gap-2 py-1.5 rounded-full bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
              user && (user.image || user.picture) ? 'pl-1 pr-2' : 'px-2'
            }`}
            onClick={() => {
              const willOpen = !(isMobileMenuOpen ?? false);
              setIsMobileMenuOpen?.(willOpen);
              // Close All Filters modal if opening mobile menu
              if (willOpen && isAllFiltersModalOpen) {
                setIsAllFiltersModalOpen?.(false);
              }
            }}
            aria-label={intl.formatMessage({
              id: 'navigation.menu',
              defaultMessage: 'Menu',
            })}
          >
            {user && (user.image || user.picture) ? (
              <>
                <div className="flex-shrink-0 overflow-hidden rounded-full">
                  <Avatar
                    src={user.image || user.picture}
                    size={32}
                    name={user.name || 'User'}
                  />
                </div>
                <Menu className="w-5 h-5 text-gray-700 dark:text-gray-300 flex-shrink-0" />
              </>
            ) : (
              <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
            )}
          </button>
        </div>
      </nav>
      
      {/* Second row: Individual facet values (virtualized horizontally) */}
      <TopFilters
        intl={intl}
        clearSearch={clearSearch}
        facetsData={{
          ...facetsData,
          location: filteredLocationItems, // Use filtered location items
        }}
        filtersObj={filtersObj}
        query={query}
      />
    </header>
  );
};

// Memoize DesktopFilterCategories to prevent unnecessary re-renders
export const DesktopFilterCategoriesMemo = memo(DesktopFilterCategories);
